import { readCsrfToken } from "@/lib/csrf"
import i18n from "@/i18n"

export {
  GRIP_DRAG_MIN_HEIGHT_CM,
  MAX_HEIGHT_CM,
  MIN_HEIGHT_CM,
  PROP_GRIP_DRAG_MIN_HEIGHT_CM,
  PROP_MIN_HEIGHT_CM,
} from "./scale-sheet-height.ts"
export const SLIDER_MAX_HEIGHT_CM = 250
/** Default diagonal size (cm) when prop has none — keep in sync with HeightParser::DEFAULT_PROP_HEIGHT_CM (was 50). */
export const DEFAULT_PROP_HEIGHT_CM = 30

export type PropScaleSheetPropRow = {
  id: number
  name: string
  saved_height_cm: number | null
  effective_height_cm: number
  front_view_url: string | null
  image_width?: number
  image_height?: number
}

export type PropScaleSheetResponse = {
  ok: boolean
  status: string
  prop_ids_key: string
  image_url: string | null
  image_download_url?: string | null
  layout_meta: {
    figures?: {
      character_id: number
      name: string
      height_cm: number
      x: number
      y: number
      w: number
      h: number
      scale_pose_url?: string | null
      hit_rect?: { x: number; y: number; w: number; h: number }
      image_width?: number
      image_height?: number
    }[]
    content_width_preview?: number
    px_per_cm_preview?: number
    characters?: {
      id: number
      image_width?: number
      image_height?: number
    }[]
    props?: {
      id: number
      image_width?: number
      image_height?: number
    }[]
  } | null
  scale_unit: string
  props: PropScaleSheetPropRow[]
  error?: string
}

export async function fetchPropScaleSheet(
  projectId: number,
  propIds?: number[],
  options?: { preview?: boolean; shotId?: number; editOnly?: boolean },
): Promise<PropScaleSheetResponse> {
  const params = new URLSearchParams()
  if (options?.shotId != null && options.shotId > 0) {
    params.set("shot_id", String(options.shotId))
  } else if (propIds && propIds.length > 0) {
    params.set("prop_ids", propIds.join(","))
  }
  if (options?.preview) {
    params.set("preview", "1")
  }
  if (options?.editOnly) {
    params.set("edit_only", "1")
  }
  const qs = params.toString()
  const res = await fetch(`/projects/${projectId}/workspace/prop_scale_sheet${qs ? `?${qs}` : ""}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": readCsrfToken() },
  })
  const data = (await res.json()) as PropScaleSheetResponse
  if (!res.ok || !data.ok) {
    throw new Error(data.error ?? i18n.t("workspace:propsPage.scaleSheetLoadError"))
  }
  return data
}

export type EnsureShotPropScaleSheetResponse = {
  ok: boolean
  status: string
  prop_ids_key: string | null
  error?: string
}

export async function ensureShotPropScaleSheet(
  projectId: number,
  shotId: number,
): Promise<Pick<EnsureShotPropScaleSheetResponse, "status" | "prop_ids_key">> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/ensure_prop_scale_sheet`, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": readCsrfToken() },
  })
  const data = (await res.json()) as EnsureShotPropScaleSheetResponse
  if (!res.ok || !data.ok) {
    throw new Error(data.error ?? i18n.t("workspace:propsPage.scaleSheetLoadError"))
  }
  return { status: data.status, prop_ids_key: data.prop_ids_key }
}

export async function patchPropHeights(
  projectId: number,
  updates: { prop_id: number; height_cm: number }[],
): Promise<void> {
  const res = await fetch(`/projects/${projectId}/workspace/prop_heights`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": readCsrfToken(),
    },
    body: JSON.stringify({ updates }),
  })
  const data = (await res.json()) as { ok?: boolean; error?: string }
  if (!res.ok || !data.ok) {
    throw new Error(data.error ?? i18n.t("workspace:propsPage.scaleSheetSaveError"))
  }
}
